import { ChangeDetectionStrategy, Component, computed, effect, inject, input, OnInit, output, signal } from "@angular/core";

import { AiModelsStore } from "@features/admin/state/ai-models.store";
import { SelectComponent, SelectOption } from "@shared/components/select/select.component";

@Component({
  selector: "component-ai-model-selector",
  imports: [SelectComponent],
  template: `
    <component-select
      [options]="modelOptions()"
      [value]="selectedOption()"
      [loading]="isLoading()"
      [placeholderText]="'Select AI Model'"
      [searchable]="false"
      [clearable]="false"
      [size]="size()"
      [dropdownCssClass]="'model-selector-dropdown'"
      [dropdownMinWidth]="'250px'"
      (singleSelectionChange)="onModelChange($event)" />
  `,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ModelSelectorComponent implements OnInit {
  readonly selectedModelId = input<string | null>(null);
  readonly size = input<"sm" | "md" | "lg">("md");
  readonly selectedModelIdChange = output<string | null>();

  // Use the store for data
  private readonly _store = inject(AiModelsStore);

  protected readonly isLoading = this._store.loading;
  protected readonly models = computed(() => this._store.models().filter((m) => m.isEnabled));

  protected readonly selectedOption = signal<SelectOption<string> | null>(null);

  // Convert AI models to select options
  protected readonly modelOptions = computed(() => {
    return this.models().map((model) => ({
      text: model.label,
      value: model.id,
      icon: this.getModelIcon(model.provider?.code)
    }));
  });

  constructor() {
    // Watch for external selectedModelId changes and update the display
    effect(() => {
      const modelId = this.selectedModelId();
      const availableModels = this.models();

      if (availableModels.length === 0) {
        // Models not loaded yet
        return;
      }

      if (modelId) {
        const model = availableModels.find((m) => m.id === modelId);
        if (model) {
          this.selectedOption.set({
            text: model.label,
            value: model.id,
            icon: this.getModelIcon(model.provider?.code)
          });
        }
      } else {
        // No model selected, clear selection
        this.selectedOption.set(null);
      }
    });
  }

  ngOnInit(): void {
    // Trigger load if not loaded
    if (!this._store.loaded() && !this._store.loading()) {
      this._store.loadModels();
    }
  }

  protected onModelChange(option: SelectOption<string> | null): void {
    if (!option) return;

    // Just emit the change - let parent component decide what to do
    this.selectedModelIdChange.emit(option.value);
  }

  private getModelIcon(providerCode?: string): string | undefined {
    switch (providerCode?.toLowerCase()) {
      case "openai":
        return "chatGpt";
      case "google":
        return "googleGemini";
      default:
        return undefined;
    }
  }
}
